import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, RefreshCw, Image, Check, AlertCircle, Sparkles } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

interface Article {
  id: string;
  headline: string;
  category: string;
  primary_token: string | null;
  tokens_featured: string[] | null;
  og_image_url: string | null;
}

export default function ContentBootstrap() {
  const [loading, setLoading] = useState<string | null>(null);
  const [generatingImages, setGeneratingImages] = useState<Set<string>>(new Set());
  const queryClient = useQueryClient();

  const { data: articles, isLoading: articlesLoading, refetch: refetchArticles } = useQuery({
    queryKey: ['admin-articles'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('articles')
        .select('id, headline, category, primary_token, tokens_featured, og_image_url')
        .eq('status', 'published')
        .order('publish_date', { ascending: false })
        .limit(20);
      
      if (error) throw error;
      return data as Article[];
    }
  });

  const runFunction = async (functionName: string, displayName: string) => {
    setLoading(functionName);
    try {
      console.log(`Running ${displayName}...`);
      const { data, error } = await supabase.functions.invoke(functionName);
      
      if (error) throw error;
      
      console.log(`${displayName} completed:`, data);
      toast.success(`${displayName} completed successfully!`, {
        description: data.message || `Processed ${data.processed || data.results?.length || 0} items`
      });
    } catch (error: any) {
      console.error(`${displayName} error:`, error);
      toast.error(`${displayName} failed`, {
        description: error.message
      });
    } finally {
      setLoading(null);
    }
  };

  const generateImageForArticle = async (article: Article) => {
    setGeneratingImages(prev => new Set(prev).add(article.id));
    
    try {
      console.log(`Generating image for: ${article.headline}`);
      
      const { data, error } = await supabase.functions.invoke('generate-article-image', {
        body: {
          articleId: article.id,
          headline: article.headline,
          category: article.category,
          primaryToken: article.primary_token,
          tokens: article.tokens_featured
        }
      });
      
      if (error) throw error;
      
      console.log(`Image generated:`, data);
      toast.success(`Image generated for "${article.headline.slice(0, 40)}..."`);
      
      // Refetch articles to update the list
      refetchArticles();
      
    } catch (error: any) {
      console.error(`Image generation error:`, error);
      
      if (error.message?.includes('429') || error.message?.includes('Rate limit')) {
        toast.error('Rate limit reached. Please wait a moment before trying again.');
      } else if (error.message?.includes('402')) {
        toast.error('AI credits exhausted. Please add credits to continue.');
      } else {
        toast.error(`Failed to generate image`, {
          description: error.message
        });
      }
    } finally {
      setGeneratingImages(prev => {
        const newSet = new Set(prev);
        newSet.delete(article.id);
        return newSet;
      });
    }
  };

  const generateAllMissingImages = async () => {
    const articlesWithoutImages = articles?.filter(a => !a.og_image_url) || [];
    
    if (articlesWithoutImages.length === 0) {
      toast.info("All articles already have images!");
      return;
    }
    
    toast.info(`Generating images for ${articlesWithoutImages.length} articles...`, {
      description: "This may take a few minutes"
    });
    
    // Generate images sequentially to avoid rate limits
    for (const article of articlesWithoutImages) {
      await generateImageForArticle(article);
      // Small delay between generations to avoid rate limits
      await new Promise(resolve => setTimeout(resolve, 2000));
    }
    
    toast.success("Image generation complete!");
  };

  const runFullWorkflow = async () => {
    setLoading("workflow");
    toast.info("Starting content generation workflow...");
    
    try {
      await runFunction("select-daily-token", "Daily Token Selection");
      toast.success("Workflow completed!", {
        description: "Daily token selected from available data"
      });
    } catch (error) {
      console.error("Workflow error:", error);
    } finally {
      setLoading(null);
    }
  };

  const articlesWithImages = articles?.filter(a => a.og_image_url).length || 0;
  const articlesWithoutImages = articles?.filter(a => !a.og_image_url).length || 0;

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold mb-2">Content Bootstrap</h1>
        <p className="text-muted-foreground">
          Manage content generation and article images
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Quick Start</CardTitle>
          <CardDescription>
            Select today's daily token from available data
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            onClick={runFullWorkflow}
            disabled={loading !== null}
            size="lg"
            className="w-full"
          >
            {loading === "workflow" ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                Selecting Daily Token...
              </>
            ) : (
              <>
                <RefreshCw className="mr-2 h-5 w-5" />
                Select Daily Token
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* AI Image Generation Section */}
      <Card className="border-primary/50 bg-primary/5">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-primary" />
                AI Article Images
              </CardTitle>
              <CardDescription className="mt-1">
                Generate social media-ready images for articles using AI
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Badge variant="secondary" className="gap-1">
                <Check className="w-3 h-3" />
                {articlesWithImages} with images
              </Badge>
              {articlesWithoutImages > 0 && (
                <Badge variant="outline" className="gap-1 border-amber-500 text-amber-500">
                  <AlertCircle className="w-3 h-3" />
                  {articlesWithoutImages} missing
                </Badge>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {articlesWithoutImages > 0 && (
            <Button
              onClick={generateAllMissingImages}
              disabled={generatingImages.size > 0}
              className="w-full gap-2"
              variant="default"
            >
              {generatingImages.size > 0 ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Generating... ({generatingImages.size} in progress)
                </>
              ) : (
                <>
                  <Image className="w-4 h-4" />
                  Generate All Missing Images ({articlesWithoutImages})
                </>
              )}
            </Button>
          )}
          
          {articlesLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
              {articles?.map(article => (
                <div
                  key={article.id}
                  className="flex items-center gap-3 p-3 rounded-lg bg-background border"
                >
                  {/* Image Preview */}
                  <div className="w-16 h-10 rounded overflow-hidden bg-muted flex-shrink-0">
                    {article.og_image_url ? (
                      <img
                        src={article.og_image_url}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Image className="w-4 h-4 text-muted-foreground" />
                      </div>
                    )}
                  </div>
                  
                  {/* Title */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{article.headline}</p>
                    <div className="flex gap-1 mt-1">
                      <Badge variant="outline" className="text-xs">
                        {article.category}
                      </Badge>
                      {article.primary_token && (
                        <Badge variant="secondary" className="text-xs">
                          {article.primary_token}
                        </Badge>
                      )}
                    </div>
                  </div>
                  
                  {/* Status / Action */}
                  <div className="flex-shrink-0">
                    {article.og_image_url ? (
                      <Badge variant="secondary" className="gap-1">
                        <Check className="w-3 h-3" />
                        Done
                      </Badge>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => generateImageForArticle(article)}
                        disabled={generatingImages.has(article.id)}
                      >
                        {generatingImages.has(article.id) ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Sparkles className="w-4 h-4" />
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-blue-500/50 bg-blue-500/5">
        <CardHeader>
          <CardTitle>Status</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <p>• Swyftx integration temporarily disabled due to API issues</p>
          <p>• Daily token selection works with existing database records</p>
          <p>• AI images are generated using Lovable AI and stored in Supabase Storage</p>
        </CardContent>
      </Card>
    </div>
  );
}
